import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { surveyService } from '../../services/survey.service';
import invitationService from '../../services/invitation.service';
import { Survey, Question } from '../../types/survey.types';
import { InvitationStats } from '../../types/invitation.types';
import ParticipantUpload from '../../components/contractor/ParticipantUpload';
import InvitationStatus from '../../components/contractor/InvitationStatus';

const visibilityLabels: Record<string, string> = {
  PUBLIC_AFTER_CLOSE: 'Público, após encerramento',
  PUBLIC_AFTER_DATE: 'Público, após data a definir',
  PARTICIPANTS_AFTER_CLOSE: 'Apenas Participantes, após Encerramento',
  PARTICIPANTS_AFTER_DATE: 'Apenas Participantes, após data a definir',
  CONTRACTOR_ONLY: 'Apenas Contratante',
};

// Phase 5 - US3: Contractor Survey Detail (T098)

const ContractorSurveyDetail = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [stats, setStats] = useState<InvitationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'invitations'>('overview');

  useEffect(() => {
    if (surveyId) {
      loadData();
    }
  }, [surveyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [surveyData, questionsData, statsData] = await Promise.all([
        surveyService.getSurvey(surveyId!),
        surveyService.getQuestions(surveyId!),
        invitationService.getStats(surveyId!),
      ]);

      setSurvey(surveyData);
      setQuestions(questionsData);
      setStats(statsData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load survey data');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    loadData(); // Reload stats after upload
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error || 'Survey not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{survey.name}</h1>
        {survey.description && (
          <p className="mt-2 text-gray-600">{survey.description}</p>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Visão Geral
          </button>
          <button
            onClick={() => setActiveTab('invitations')}
            className={`${
              activeTab === 'invitations'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Convites
          </button>
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Survey Info */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Informações da Pesquisa</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="text-base font-medium">{survey.status}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Período</p>
                <p className="text-base font-medium">
                  {new Date(survey.startDate).toLocaleDateString('pt-BR')} -{' '}
                  {new Date(survey.endDate).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Abrangência</p>
                <p className="text-base font-medium">{survey.geographicScope}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Visibilidade dos Resultados</p>
                <p className="text-base font-medium">
                  {visibilityLabels[survey.visibilityLevel] || survey.visibilityLevel}
                </p>
              </div>
              {survey.disclosureDate && (
                <div>
                  <p className="text-sm text-gray-500">Data da Divulgação</p>
                  <p className="text-base font-medium">
                    {new Date(survey.disclosureDate).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              )}
              {survey.surveyValue && (
                <div>
                  <p className="text-sm text-gray-500">Valor</p>
                  <p className="text-base font-medium">
                    R$ {survey.surveyValue.toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Invitation Stats */}
          {stats && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Estatísticas de Convites</h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                  <p className="text-sm text-gray-500">Total</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
                  <p className="text-sm text-gray-500">Pendentes</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-600">{stats.sent}</p>
                  <p className="text-sm text-gray-500">Enviados</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600">{stats.delivered}</p>
                  <p className="text-sm text-gray-500">Entregues</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-red-600">{stats.failed}</p>
                  <p className="text-sm text-gray-500">Falhas</p>
                </div>
              </div>
            </div>
          )}

          {/* Questions */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Perguntas ({questions.length})</h2>
            <div className="space-y-3">
              {questions.map((question) => (
                <div key={question.id} className="border-l-4 border-blue-500 pl-4">
                  <p className="font-medium">{question.shortDescription}</p>
                  {question.longDescription && (
                    <p className="text-sm text-gray-600 mt-1">
                      {question.longDescription}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs px-2 py-1 bg-gray-100 rounded">
                      {question.questionType}
                    </span>
                    {question.required && (
                      <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded">
                        Obrigatória
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <div className="space-y-6">
          {/* Upload Component */}
          <ParticipantUpload
            surveyId={surveyId!}
            onSuccess={handleUploadSuccess}
          />

          {/* Invitations List */}
          <InvitationStatus surveyId={surveyId!} />
        </div>
      )}
    </div>
  );
};

export default ContractorSurveyDetail;
